// packages
import { h } from 'preact';
import * as d3 from 'd3';
import { highways, cities } from '../utils/map-utils';
import createBase from '../utils/d3-base';
import * as topojson from 'topojson-client';
import topology from 'Assets/geography/texas_counties_roads.json';
// import { swoopyDrag } from '../swoopy-drag';
// import { wordwrap } from 'd3-jetpack';

// data
import unemploymentRateSnapshots from '../../../data/map/county_unemployment_map_all_snapshots.json';

// components
import PureComponent from './PureComponent';

class MiniChart extends PureComponent {
  constructor() {
    super();

    this.getContainerWidth = this.getContainerWidth.bind(this);
    this.createChart = this.createChart.bind(this);
    this.redrawChart = this.redrawChart.bind(this);

    this.state = {
      containerWidth: null,
      bodyWidth: null,
      smallBreakPoint: 544,
      mediumBreakPoint: 768
    };
  }

  createChart(date) {
    let { containerWidth, bodyWidth, smallBreakPoint, mediumBreakPoint } = this.state;

    let data = unemploymentRateSnapshots.filter(datum => datum.date == date);
    let allData = unemploymentRateSnapshots;

    // Make object into array so we can find the domain of data for color scale
    const rateData = d3.map();
    data.forEach(d => rateData.set(d.fips, +d.value));

    // get data array from all snapshots for scale
    let dataArray = allData.map(d => d.value);

    let color = d3
      .scaleQuantize()
      .nice()
      .range([
        '#edf6f7',
        '#c3e1e7',
        '#84c0ce',
        '#3a9bb1',
        '#267a8c',
        '#0a4b56',
      ]);

    // sets geo stuff for projection
    const geoPath = d3.geoPath();
    const geoIdentity = d3.geoIdentity().reflectY(true);

    let chartWidth = containerWidth / 4 - 20;
    if (bodyWidth < mediumBreakPoint) chartWidth = containerWidth / 2 - 20;
    if (bodyWidth < smallBreakPoint) chartWidth = containerWidth;

    let isMobile = bodyWidth < smallBreakPoint;

    // uses `./utils/d3-base` to set up a bunch of conventional d3 stuff
    const map = createBase({
      container: d3.select(`.mini-map-${date.replace(' ', '-')}`), // the chart container
      width: chartWidth,
      height: chartWidth,
    });

    // Create svg for the map
    const svg = map.g
      .append('g')
      .attr('class', 'g_rate_map')
      .attr('transform', 'translate(-8, 0)');

    // create map once files are loaded
    let promises = [topology];
    Promise.all(promises).then(ready);

    function ready([shapefiles]) {
      // Creates title and map base for the rate map
      const state = topojson.merge(
          shapefiles,
          shapefiles.objects.counties.geometries
        ),
        counties = topojson.feature(shapefiles, shapefiles.objects.counties),
        texas_highways = topojson.feature(shapefiles, shapefiles.objects.roads),
        major_cities = topojson.feature(shapefiles, shapefiles.objects.places);

      const numberOfClasses = color.range().length - 1;

      // get the extent of data from all snapshots to find a range for domain
      let dataRange = d3.extent(dataArray, d => d);

      let x_legend = d3.scaleLinear().rangeRound([
        map.width * (isMobile ? 0.1 : 0.58), //0.58
        map.width * (isMobile ? 0.9 : 0.825), //0.825
      ]);

      // Establish the domain
      color.domain(dataRange);
      // how to get the thresholds for a bucket:
      // console.log('first bucket', color.invertExtent('#edf6f7'));
      // console.log('last bucket', color.invertExtent('#0a4b56'));
      // console.log('second bucket', color.invertExtent('#c3e1e7'));
      // console.log('third bucket', color.invertExtent('#84c0ce'));

      // x_legend.domain(ckmeansBreaks);
      x_legend.domain(dataRange);

      const projection = geoIdentity.fitExtent(
        [
          [0, 0],
          [map.width, map.height],
        ],
        state
      );
      const path = geoPath.projection(projection);

      // Add Texas county shapefile
      svg
        .selectAll('g')
        .data(counties.features)
        .enter()
        .append('path')
        .attr('class', 'counties')
        .attr('fill', function(d) {
          d.rate = rateData.get(d.properties.fips); // ||0
          return color(d.rate);
        })
        .attr('stroke', 'rgba(255, 255, 255, .6)')
        .attr('d', path);

      // Adds highways (the order of this is very important)
      highways(svg, texas_highways, path);

      let filtered_cities = major_cities.features.filter(d =>
        ['Dallas', 'Houston', 'San Antonio'].includes(d.properties.name)
      );
      major_cities.features = filtered_cities;

      // Add cities
      if (date == 'February 2020') cities(svg, major_cities, path);

      // if (date == 'July 2020') {
      //   // arrow marker, need to append to the svg first to use
      //   svg
      //     .append('marker')
      //     .attr('id', 'arrow')
      //     .attr('viewBox', '-10 -10 20 20')
      //     .attr('markerWidth', 10)
      //     .attr('markerHeight', 10)
      //     .attr('fill', 'hsl(0, 0%, 29%)')
      //     .attr('orient', 'auto-start-reverse')
      //     .append('path')
      //     .attr('d', 'M-6.75,-6.75 L 0,0 L -6.75,6.75');

      //   let swoopySel = svg.append('g').attr('class', 'swoopy-drag-lines');

      //   // annotations for swoopyDrag
      //   let annotations = [
      //     {
      //       path: 'M19,57C20,70,5,74,-2,70',
      //       text: 'Starr County',
      //       textOffset: [-75, 72],
      //     },
      //   ];

      //   let swoopy = swoopyDrag()
      //     .x(() => map.width / 2)
      //     .y(() => map.height / 1.5)
      //     .draggable(true)
      //     .annotations(annotations);

      //   swoopySel.call(swoopy).attr('font-size', '12px');

      //   swoopySel.selectAll('text').each(function(d) {
      //     d3.select(this)
      //       .text('')
      //       .tspans(wordwrap(d.text, 24));
      //   });

      //   swoopySel.selectAll('path').attr('marker-start', 'url(#arrow)');
      // }
    }
  }

  redrawChart() {
    const { date } = this.props;

    d3.selectAll(`.mini-map-${date.replace(' ', '-')} svg`).remove();

    let { containerWidth, bodyWidth } = this.getContainerWidth();
    this.setState({ containerWidth: containerWidth, bodyWidth: bodyWidth });

    this.createChart(date);
  }

  getContainerWidth() {
    let base = d3.select('.mini-map-grid');
    const body = d3.select('body');

    const containerWidth = base.node().offsetWidth;
    const bodyWidth = body.node().offsetWidth;

    return { containerWidth, bodyWidth };
  }

  componentDidMount() {
    const { date } = this.props;
    let { containerWidth, bodyWidth } = this.getContainerWidth();

    this.setState(
      { containerWidth: containerWidth, bodyWidth: bodyWidth },
      () => {
        this.createChart(date);
      }
    );

    window.addEventListener('resize', () => {
      this.redrawChart();
    });
  }

  componentWillUnmount() {
    window.addEventListener('resize', () => {
      this.redrawChart();
    });
  }

  render() {
    const { date } = this.props;

    return <div class={`mini-map mini-map-${date.replace(' ', '-')}`}></div>;
  }
}

export default MiniChart;
